import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from 'recharts';
import { ChartAIItem } from '../../hooks/useChatStore';

interface ChartAIProps {
  chart: ChartAIItem;
}

const ChartAI: React.FC<ChartAIProps> = ({ chart }) => {
  const makeXYData = () =>
    (chart.data.labels ?? []).map((label, i) => ({
      name: label,
      ...chart.data.datasets!.reduce(
        (acc, ds) => ({
          ...acc,
          [ds.label]: ds.data[i],
        }),
        {},
      ),
    }));

  switch (chart.type) {
    case 'bar_chart': {
      const data = makeXYData();
      return (
        <BarChart
          data={data}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          width={500}
          height={300}
        >
          {chart.title && (
            <text x={0} y={0} dy={-8}>
              {chart.title}
            </text>
          )}
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick />
          <YAxis />
          <Tooltip />
          <Legend />
          {chart.data.datasets!.map((ds, idx) => (
            <Bar
              key={ds.label}
              dataKey={ds.label}
              name={ds.label}
              fill={
                Array.isArray(ds.backgroundColor)
                  ? ds.backgroundColor?.[idx]
                  : ds.backgroundColor
              }
              stroke={
                Array.isArray(ds.borderColor)
                  ? ds.borderColor?.[idx]
                  : ds.borderColor
              }
              strokeWidth={ds.borderWidth}
            />
          ))}
        </BarChart>
      );
    }

    case 'line_chart': {
      const data = makeXYData();
      return (
        <LineChart
          data={data}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          width={500}
          height={300}
        >
          {chart.title && (
            <text x={0} y={0} dy={-8}>
              {chart.title}
            </text>
          )}
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          {chart.data.datasets!.map((ds, idx) => (
            <Line
              key={ds.label}
              type="monotone"
              dataKey={ds.label}
              name={ds.label}
              stroke={
                Array.isArray(ds.borderColor)
                  ? ds.borderColor?.[idx]
                  : ds.borderColor
              }
              strokeWidth={ds.borderWidth}
            />
          ))}
        </LineChart>
      );
    }

    case 'pie_chart': {
      const pieData = chart.data.pieData!;
      return (
        <PieChart
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          width={500}
          height={300}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            outerRadius={80}
            label
          >
            {pieData.map(entry => (
              <Cell key={entry.label} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number, name: string) => [`${value}`, name]}
          />
          <Legend verticalAlign="bottom" height={36} />
        </PieChart>
      );
    }
    default:
      return null;
  }
};

export default ChartAI;
